import React, { useEffect, useState } from "react";
import styles from "./HomePage.module.scss";
import { useTranslation } from "react-i18next";
import Grid from "../../config/Grid/Grid";
import IconButton from "../../components/IconButton/IconButton";
import LanguageSwitch from "../../components/LanguageSwitch/LanguageSwitch";
import TextComponent from "../../components/TextComponent/TextComponent";
import { useBackdropState } from "../../components/BackDrop/BackDropProvider";
import { useVenuesDataStore } from "@/store/useVenuesDataStore";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { accountPath } from "@/routes/paths";
import { useLoyaltyActions } from "@/store/useLoyaltyStore";

interface HomePageProps {
  children: React.ReactNode;
  paid: boolean;
}
function HomePage(props: HomePageProps) {
  const { children } = props;
  const { openedBy, updateOpenedBy, removeOpenedBy } = useBackdropState();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { expireIfStale } = useLoyaltyActions();

  // Keeps the dot honest: a balance past its validity window is dropped before
  // it can advertise points the guest no longer has.
  useEffect(() => {
    expireIfStale();
  }, [expireIfStale]);
  const { restaurantId = "", tableId = "" } = useParams<{
    restaurantId: string;
    tableId: string;
  }>();

  // Collapse the menu on navigation, so the header never stays expanded after
  // the backdrop provider drops its tag.
  useEffect(() => {
    setIsMenuOpen(false);
  }, [pathname]);

  const toggleMenu = () => {
    if (openedBy.includes("menu")) {
      removeOpenedBy("menu");
    } else {
      updateOpenedBy("menu");
    }
    setIsMenuOpen(!isMenuOpen);
  };

  const { t } = useTranslation();

  // Real venue details from the mobile-app branding, falling back to the
  // generic copy when a restaurant has not filled them in.
  const venue = useVenuesDataStore((state) => state.venue);
  const venueName = venue?.name || t("homePage.header.venueName");
  const websiteText = venue?.websiteText || t("homePage.header.visitOurWebsite");
  // The RMS stores bare hosts like "www.example.com"; without a scheme the
  // browser would treat that as a path on our own origin.
  const websiteUrl = venue?.websiteUrl
    ? /^https?:\/\//i.test(venue.websiteUrl)
      ? venue.websiteUrl
      : `https://${venue.websiteUrl}`
    : "https://menupos.pl";

  return (
    <>
      <div className={styles.backgroundImage} />
      <div
        className={`${styles.headerImage} ${isMenuOpen ? styles.menuOpen : ""}`}
      />
      <div
        className={`${styles.headerImageOverlay} ${
          isMenuOpen ? styles.headerImageOverlayVisible : ""
        }`}
      />
      <div className={styles.venueLogoImage} />
      <Grid>
        <div className={`start-1 col-4 align-start ${styles.homePageWrapper}`}>
          <div className={`${styles.headerWrapper}`}>
            <div className={`${styles.headerButtonsContainer}`}>
              <IconButton
                iconName={`${
                  isMenuOpen ? "las la-times" : "las la-bars"
                } icon-big`}
                onClick={toggleMenu}
                color={isMenuOpen ? "secondary" : "primary"}
              />
              <div className={styles.headerRightButtons}>
                <LanguageSwitch dark={isMenuOpen} />
                <IconButton
                  iconName="las la-user-circle icon-big"
                  onClick={() => navigate(accountPath(restaurantId, tableId))}
                  color={isMenuOpen ? "secondary" : "primary"}
                />
              </div>
            </div>

            <div
              className={`${styles.headerTextContainer} ${
                isMenuOpen ? styles.headerTextContainerOpen : ""
              }`}
            >
              <p className={`title-bold-large ${styles.headerVenueName}`}>
                {venueName}
              </p>
              <TextComponent
                text={websiteText}
                fontSize="body-bold-small"
                color={"secondary"}
                underline
                uppercase
                onClick={() => {
                  window.open(websiteUrl, "_blank");
                }}
              />
            </div>
          </div>
          {children}
          <div className={`${styles.footerContainer}`}>
            <div className={styles.footerTitleContainer}>
              <p className={`body-small ${styles.footerTitleText}`}>
                {t("homePage.footer.title.firstPart")}
              </p>
              <p className={`body-small-logo ${styles.footerTitleText}`}>
                {t("homePage.footer.title.secondPart")}
              </p>
            </div>

            <p className={`body-small ${styles.footerDescription}`}>
              {t("homePage.footer.description.firstPart")}
              <mark className="mark-underlined">
                {t("homePage.footer.description.secondPart")}
              </mark>
              {t("homePage.footer.description.thirdPart")}
              <mark className="mark-underlined">
                {t("homePage.footer.description.fourthPart")}
              </mark>
              {t("homePage.footer.description.fifthPart")}
            </p>
          </div>
        </div>
      </Grid>
    </>
  );
}

export default HomePage;
